import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth'
import { useAlerts } from '@/features/insights/hooks/use-insights'
import { AlertTriangle, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const severityColors: Record<string, string> = {
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

const severityLabels: Record<string, string> = {
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

export function InsightsPanel() {
  const { currentOrganization } = useAuth()

  const isAssistant = currentOrganization?.userRole === 'assistant'
  const { data: alerts, isLoading } = useAlerts({ status: 'active' })
  const topAlerts = (alerts ?? []).slice(0, 5)

  // Hide for assistants (after hooks)
  if (isAssistant) return null

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-5 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5" />
            Alertas ativos
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between p-5 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-5 w-5" />
          Alertas ativos
        </CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/insights">
            Ver todos
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="p-5 pt-0">
        {topAlerts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Nenhum alerta ativo
          </p>
        ) : (
          <div className="space-y-3">
            {topAlerts.map((alert) => (
              <Link
                key={alert.id}
                to="/insights"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{alert.insight_text}</p>
                  <p className="text-xs text-muted-foreground">
                    {alert.patient.full_name}
                  </p>
                </div>
                <Badge
                  className={cn(
                    'ml-2',
                    severityColors[alert.severity] ?? 'bg-gray-100 text-gray-800'
                  )}
                >
                  {severityLabels[alert.severity] ?? alert.severity}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
